import { Logout } from '@ori-ui/icons';
import { styled, useMediaQuery } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import { type FC, useCallback, useEffect, useState } from 'react';

import { LocalizationItem } from '../constants/LocalizationItem';
import { BREAKPOINT_HORIZONTAL_SIDEPANEL } from '../constants/breakpoints';
import { useLogout } from '../hooks/useLogout';
import { useMenuItemsFilter } from '../hooks/useMenuItemsFilter';
import { usePersonalMenu, useSelectedMenuItem } from '../hooks/usePersonalMenu';
import type { ClickedItem } from '../models';
import { AccordionMenu } from './AccordionMenu';
import { FormattedMessage } from './FormattedMessage';
import { Personalisation } from './Personalisation';
import { SkeletonLoader } from './SkeletonLoader';

const Container = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  boxSizing: 'border-box',
  maxWidth: theme.spacing(66),
  backfaceVisibility: 'initial',
  transition: 'height var(--top-nav-height-animation),background-color 0.1s ease-in-out',
  zIndex: 999,
  '&::WebkitFontSmoothing': 'antialiased',
  '& a': {
    textDecoration: 'none',
    marginLeft: 0,
  },
  '& button': {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: theme.spacing(10),
    border: 'none',
    fontFamily: 'SansOri, Arial, Helvetica, sans-serif',
    cursor: 'pointer',
    '&:before': {
      marginRight: theme.spacing(3),
      fontSize: '27px',
    },
  },
  [theme.breakpoints.up(BREAKPOINT_HORIZONTAL_SIDEPANEL)]: {
    height: 'auto',
    position: 'fixed',
    top: 'var(--top-nav-height, 0)',
    bottom: 0,
    left: 0,
    width: theme.spacing(56),
    maxWidth: '100%',
    backfaceVisibility: 'initial',
    transition: 'unset',
    transitionProperty: 'unset',
    '& a': {
      textDecoration: 'none',
    },
  },
  '& ul': {
    margin: 0,
    padding: 0,
  },
}));

const Scroller = styled('div')(({ theme }) => ({
  boxSizing: 'border-box',
  height: '100%',
  paddingTop: theme.spacing(3),
  overflow: 'hidden auto',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
  MsOverflowStyle: '-ms-autohiding-scrollbar',
  scrollbarWidth: 'thin',
  scrollbarColor: theme.palette.grey[300],
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey[300],
    backgroundClip: 'border-box',
    WebkitBackgroundClip: 'border-box',
    '&:hover': {
      backgroundColor: theme.palette.grey[400],
    },
    '&:active': {
      backgroundColor: theme.palette.blueGrey[200],
    },
    '&:vertical': {
      minHeight: theme.spacing(2),
    },
    '&:horizontal': {
      minWidth: theme.spacing(2),
    },
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  [theme.breakpoints.down(500)]: {
    '&::-webkit-scrollbar': {
      width: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.grey[300],
    },
  },
}));

const Button = styled('button')(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  alignItems: 'center',
  width: '100%',
  height: theme.spacing(10),
  padding: theme.spacing(2, 3),
  overflow: 'hidden',
  background: '#f0f3f0',
  boxShadow: theme.shadows[2],
  color: theme.palette.grey[700],
  fontSize: '15px',
  fontWeight: 'bold',
  letterSpacing: theme.spacing(0.1),
  cursor: 'pointer',
  userSelect: 'none',
  margin: 0,
  '&:before': {
    marginRight: theme.spacing(3),
    fontSize: '27px',
  },
  '&:hover': {
    background: '#e8ece8',
    boxShadow: theme.shadows[4],
  },
}));

declare global {
  interface Ori {
    vietnamELearning?: {
      updateMenuWithVietnam: () => void;
    };
  }
}

export const PersonalMenuContent: FC = () => {
  const isDesktop = useMediaQuery(`(min-width:${BREAKPOINT_HORIZONTAL_SIDEPANEL}px)`);
  const logout = useLogout();
  const { getTestId } = useTestId();
  const [menuClickedItem, setMenuClickedItem] = useState<ClickedItem>({
    path: '',
    open: false,
  });

  const { data } = usePersonalMenu();
  const { user, items, hasSubitems } = data ?? {};
  const selectedPath = useSelectedMenuItem();

  const {
    filteredItems: menuItems,
    upgradeToConsultantUrl,
    upgradeToBrandPartnerUrl,
  } = useMenuItemsFilter(items ?? [], isDesktop);

  useEffect(() => {
    if (selectedPath) {
      setMenuClickedItem({
        path: selectedPath,
        open: true,
      });
    }
  }, [selectedPath]);

  const handleMenuClick = useCallback((event: React.SyntheticEvent, clickedItem: ClickedItem): void => {
    event.preventDefault();
    setMenuClickedItem(clickedItem);
  }, []);

  useEffect(() => {
    if (hasSubitems) {
      window.ori?.vietnamELearning?.updateMenuWithVietnam();
    }
  }, [hasSubitems]);

  // This will normally be handled with suspense instead and this serves as type cheing
  if (!data) {
    return <SkeletonLoader />;
  }

  return (
    <Container data-testid={getTestId('wrapper')}>
      <Scroller>
        {!isDesktop && user ? (
          <Personalisation
            user={user}
            upgradeToConsultantUrl={upgradeToConsultantUrl}
            upgradeToBrandPartnerUrl={upgradeToBrandPartnerUrl}
          />
        ) : null}
        <AccordionMenu
          menuItems={menuItems}
          menuClickedItem={menuClickedItem}
          onMenuClick={handleMenuClick}
        />
      </Scroller>
      <Button
        type="button"
        data-testid={getTestId('logout')}
        onClick={logout}
      >
        {/* eslint-disable-next-line react/jsx-no-bind -- this is an Emotion style */}
        <Logout sx={(theme) => ({ marginRight: theme.spacing(3) })} />
        <FormattedMessage id={LocalizationItem.SignOut} />
      </Button>
    </Container>
  );
};
